import * as React from 'react'
import * as ReactDOM from 'react-dom'
import { useForkRef } from '@applift/factor'
import { useGridApiContext } from '../../utils/useGridApiContext'
import { useGridSelectorAdvanced } from '../../utils/useGridSelector'
import { gridTotalHeaderHeightSelector } from '../columnGrouping/gridColumnGroupsSelector'
import { useGridApiEventHandler } from '../../utils/useGridApiEventHandler'
import { GridEventListener } from '../../../models'
import { useGridRootProps } from '../../utils/useGridRootProps'
import { GridScrollParams } from '../../../models/params/gridScrollParams'

interface UseGridColumnHeadersProps {
  innerRef?: React.Ref<HTMLDivElement>
}

function isUIEvent(event: any): event is React.UIEvent {
  return !!event.target
}

export const useGridColumnHeaders = (props: UseGridColumnHeadersProps) => {
  const { innerRef: innerRefProp } = props
  const [dragCol, setDragCol] = React.useState('')
  const [resizeCol, setResizeCol] = React.useState('')
  const apiRef = useGridApiContext()
  const rootProps = useGridRootProps()
  const innerRef = React.useRef<HTMLDivElement>(null)
  const handleInnerRef = useForkRef(innerRefProp, innerRef)
  const totalHeaderHeight = useGridSelectorAdvanced(
    apiRef,
    rootProps,
    gridTotalHeaderHeightSelector
  )
  const [renderContext, setRenderContext] =
    React.useState<GridScrollParams | null>(null)

  React.useEffect(() => {
    apiRef.current.columnHeadersContainerElementRef!.current!.scrollLeft = 0
  }, [apiRef])

  const prevRenderContext = React.useRef<GridScrollParams | null>(renderContext)
  const prevScrollLeft = React.useRef(0)

  /* need to refactor */
  const tableInstance = apiRef.current.tableInstance
  /* end of need to refactor */

  const updateInnerPosition = React.useCallback(
    (nextRenderContext: GridScrollParams) => {
      innerRef!.current!.style.transform = `translate3d(${
        nextRenderContext.columnOffset - nextRenderContext.left
      }px,0px,0px)`
    },
    []
  )

  const handleScroll = React.useCallback<
    GridEventListener<'scrollPositionChange'>
  >(
    (scrollParams, event) => {
      const { left, columnOffset, colRange } = scrollParams
      if (!innerRef?.current) {
        return
      }
      // // Ignore vertical scroll.
      // // Excepts the first event which sets the previous render context.
      if (
        prevScrollLeft.current === left &&
        prevRenderContext.current?.colRange[0]?.index === colRange[0]?.index &&
        prevRenderContext.current?.colRange[
          prevRenderContext.current.colRange.length - 1
        ]?.index === colRange[colRange.length - 1]?.index &&
        prevRenderContext.current?.columnOffset === columnOffset
      ) {
        return
      }
      prevScrollLeft.current = left

      // We can only update the position when we guarantee that the render context has been
      // rendered. This is achieved using ReactDOM.flushSync or when the context doesn't change.
      let canUpdateInnerPosition = false

      if (
        prevRenderContext.current?.colRange[0]?.index !== colRange[0]?.index ||
        prevRenderContext.current?.colRange[
          prevRenderContext.current.colRange.length - 1
        ]?.index !== colRange[colRange.length - 1]?.index ||
        !prevRenderContext.current
      ) {
        // ReactDOM.flushSync cannot be called on `scroll` events fired inside effects
        if (isUIEvent(event)) {
          // To prevent flickering, the inner position can only be updated after the new context has
          // been rendered. ReactDOM.flushSync ensures that the state changes will happen before
          // updating the position.
          ReactDOM.flushSync(() => {
            setRenderContext(scrollParams)
          })
          canUpdateInnerPosition = true
        } else {
          setRenderContext(scrollParams)
        }
        prevRenderContext.current = scrollParams
      } else {
        canUpdateInnerPosition = true
      }

      if (scrollParams && canUpdateInnerPosition) {
        updateInnerPosition(scrollParams)
      }
    },
    [updateInnerPosition]
  )

  React.useLayoutEffect(() => {
    if (renderContext) {
      updateInnerPosition(renderContext)
    }
  }, [renderContext, updateInnerPosition])

  const handleColumnResizeStart = React.useCallback<
    GridEventListener<'columnResizeStart'>
  >(params => setResizeCol(params.field), [])
  const handleColumnResizeStop = React.useCallback<
    GridEventListener<'columnResizeStop'>
  >(() => setResizeCol(''), [])

  const handleColumnReorderStart = React.useCallback<
    GridEventListener<'columnHeaderDragStart'>
  >(params => setDragCol(params.field), [])

  const handleColumnReorderStop = React.useCallback<
    GridEventListener<'columnHeaderDragEnd'>
  >(() => setDragCol(''), [])

  useGridApiEventHandler(apiRef, 'columnResizeStart', handleColumnResizeStart)
  useGridApiEventHandler(apiRef, 'columnResizeStop', handleColumnResizeStop)
  useGridApiEventHandler(
    apiRef,
    'columnHeaderDragStart',
    handleColumnReorderStart
  )
  useGridApiEventHandler(apiRef, 'columnHeaderDragEnd', handleColumnReorderStop)
  useGridApiEventHandler(apiRef, 'scrollPositionChange', handleScroll)

  const rootStyle = {
    minHeight: totalHeaderHeight,
    maxHeight: totalHeaderHeight,
  }

  return {
    getRootProps: (other = {}) => ({ style: rootStyle, ...other }),
    getInnerProps: () => ({
      ref: handleInnerRef,
      role: 'rowgroup',
    }),
    isDragging: !!dragCol,
    dragCol,
    resizeCol,
    renderConext: {
      tableInstance,
      renderContext,
      disableExtendRowFullWidth: rootProps.disableExtendRowFullWidth,
      showColumnRightBorder: rootProps.showColumnRightBorder,
    },
  }
}
